import type { AudioEngine } from './AudioEngine.js';

/**
 * The song itself: one decoded buffer and the source node playing it.
 *
 * `scheduleStart` takes an absolute context timestamp rather than a delay, and
 * that timestamp is the same one handed to `MusicClock.startAt`. Sharing the
 * origin is what makes the clock exact instead of approximate — starting audio
 * "now" and separately noting "now" leaves a gap of however long the two
 * statements took, which is precisely the error a rhythm game cannot absorb.
 *
 * A `AudioBufferSourceNode` is single-use by specification, so a new one is
 * created per playback rather than reused.
 */
export class MusicTrack {
  private source: AudioBufferSourceNode | null = null;
  private startedAt: number | null = null;
  private ended = false;
  audioStartRequested = false;
  audioSourceStarted = false;
  audioError: string | null = null;

  /** Source playback position, not merely a running context or simulation clock. */
  get playbackTime(): number {
    return !this.audioSourceStarted || this.startedAt === null ? 0 :
      Math.min(this.duration, Math.max(0, this.engine.context.currentTime - this.startedAt));
  }

  constructor(
    private readonly engine: AudioEngine,
    readonly buffer: AudioBuffer,
  ) {}

  /** Song length in seconds, from the decoded buffer rather than any metadata. */
  get duration(): number {
    return this.buffer.duration;
  }

  get sampleRate(): number {
    return this.buffer.sampleRate;
  }

  get channels(): number {
    return this.buffer.numberOfChannels;
  }

  get playing(): boolean {
    return this.source !== null && !this.ended;
  }

  /** Context time playback was scheduled for, or null when it has not been. */
  get scheduledStart(): number | null {
    return this.startedAt;
  }

  /**
   * Schedules playback at an absolute `AudioContext.currentTime` value.
   *
   * @returns the timestamp actually used, so the caller can anchor the clock to it.
   */
  scheduleStart(contextTime: number): number {
    this.stop();
    this.audioStartRequested = true;
    this.audioError = null;

    try {
      const source = this.engine.context.createBufferSource();
      this.source = source;
      source.buffer = this.buffer;
      source.connect(this.engine.music);
      source.onended = () => { this.ended = true; };

      // Use the identical context timestamp for the source and MusicClock.
      const when = Math.max(contextTime, this.engine.context.currentTime);
      source.start(when);
      this.audioSourceStarted = true;
      this.startedAt = when;
      this.ended = false;
      return when;
    } catch (cause) {
      this.audioError = `Audio source start: ${String(cause)}`;
      this.source?.disconnect();
      this.source = null;
      this.audioSourceStarted = false;
      throw cause;
    }
  }

  stop(): void {
    this.audioSourceStarted = false;
    if (this.source === null) return;
    try {
      this.source.onended = null;
      this.source.stop();
    } catch {
      // Stopping a node that never started throws; nothing to recover from.
    }
    this.source.disconnect();
    this.source = null;
    this.ended = false;
  }

  dispose(): void {
    this.stop();
    this.startedAt = null;
  }
}
